import type { UiTestScenarioId, UiTestScenarioSummary } from '@/modules/ui-test/uiTest.types'
import {
  buildEntityUiTestPlan,
  describeEntityUiTestPlan,
  generateEditTen,
  refreshEntityUiTestPlan,
  rowMa,
  rowTen,
  type EntityUiTestPlan,
} from '@/modules/ui-test/scenarios/entityUiTestPlan'
import {
  clickDialogConfirmButton,
  clickDialogSaveButton,
  clickElement,
  fillFieldValue,
  findFieldByName,
  findInRoot,
  findRowByMa,
  findRowContaining,
  pressEnter,
  typeSlowly,
  waitFor,
  waitForAsync,
  waitForDialogClosed,
  waitForTopDialog,
} from '@/modules/ui-test/utils/uiTestDomActions'
import { runUiTestSuite, type UiTestRunner } from '@/modules/ui-test/utils/uiTestRunner'
import { buildMaSet, buildTenSet, normKey, stamp } from '@/modules/test-data/testDataSeedUtils'

export type EntityUiTestEditPattern = 'list-modal' | 'route-modal' | 'route-page'
export type EntityUiTestDeleteMode = 'direct' | 'confirm' | 'skip'
export type EntityUiTestRowSelectMode = 'select' | 'visible-only'

export type EntityUiTestModuleConfig = {
  scenarioId: UiTestScenarioId
  suiteLabel: string
  listPath: string
  resolveListPath?: () => Promise<string>
  testPrefix: string
  maTokenPrefix: string
  fetchAllRows: () => Promise<Array<Record<string, unknown>>>
  rowMaKey: string
  rowTenKey: string
  formMaField: string
  formTenField: string
  formGhiChuField?: string
  editPattern: EntityUiTestEditPattern
  rowSelectMode?: EntityUiTestRowSelectMode
  deleteMode?: EntityUiTestDeleteMode
  skipCreate?: boolean
  skipEdit?: boolean
  skipDelete?: boolean
  skipSearch?: boolean
  maRequiredOnCreate?: boolean
  maEditableOnEdit?: boolean
}

function addBtn(prefix: string): string {
  return `[data-ui-test-id="${prefix}-add-btn"]`
}

function searchBar(prefix: string): string {
  return `[data-ui-test-id="${prefix}-search-bar"]`
}

function rowEditSelector(prefix: string, ma: string): string {
  return `[data-ui-test-id="${prefix}-edit-${CSS.escape(ma)}"] button`
}

function rowDeleteSelector(prefix: string, ma: string): string {
  return `[data-ui-test-id="${prefix}-delete-${CSS.escape(ma)}"] button`
}

function buttonLabel(btn: Element): string {
  return `${btn.getAttribute('aria-label') ?? ''} ${btn.textContent ?? ''}`.replace(/\s+/g, ' ').trim()
}

function findRowEditButton(row: HTMLElement, prefix: string, ma: string): HTMLElement | null {
  const byId = row.querySelector(rowEditSelector(prefix, ma)) as HTMLElement | null
  if (byId) return byId
  return (
    (Array.from(row.querySelectorAll('button')).find((btn) =>
      /sửa|chỉnh sửa|edit|修改/i.test(buttonLabel(btn)),
    ) as HTMLElement | undefined) ?? null
  )
}

function findRowDeleteButton(row: HTMLElement, prefix: string, ma: string): HTMLElement | null {
  const byId = row.querySelector(rowDeleteSelector(prefix, ma)) as HTMLElement | null
  if (byId) return byId
  return (
    (Array.from(row.querySelectorAll('button')).find((btn) =>
      /xóa|xoá|delete|删除/i.test(buttonLabel(btn)),
    ) as HTMLElement | undefined) ?? null
  )
}

function screenOf(uiTest: UiTestRunner, label: string): HTMLElement {
  const screen = uiTest.screenRootRef.current
  if (!screen) throw new Error(`Không tìm thấy vùng màn hình ${label}`)
  return screen
}

function findRow(screen: HTMLElement, ma: string): HTMLElement | null {
  return findRowByMa(screen, ma) ?? findRowContaining(screen, ma)
}

async function waitForListReady(uiTest: UiTestRunner, cfg: EntityUiTestModuleConfig): Promise<void> {
  await waitFor(
    () => {
      const screen = uiTest.screenRootRef.current
      if (!screen) return false
      if (screen.querySelector(addBtn(cfg.testPrefix))) return true
      if (screen.querySelector(searchBar(cfg.testPrefix))) return true
      return Boolean(screen.querySelector('[role="progressbar"]') === null && screen.querySelector('table'))
    },
    20_000,
    200,
  )
}

async function resolveListPath(cfg: EntityUiTestModuleConfig): Promise<string> {
  if (cfg.resolveListPath) return cfg.resolveListPath()
  return cfg.listPath
}

async function goToCleanList(uiTest: UiTestRunner, cfg: EntityUiTestModuleConfig): Promise<void> {
  const path = await resolveListPath(cfg)
  if (window.location.pathname !== path || window.location.search) {
    uiTest.navigate(path, { replace: true })
    await waitFor(() => window.location.pathname === path && !window.location.search, 8000, 100)
  }
  await waitForListReady(uiTest, cfg)
}

async function createOneViaUi(
  uiTest: UiTestRunner,
  cfg: EntityUiTestModuleConfig,
  item: EntityUiTestPlan['createItems'][number],
): Promise<{ ok: boolean; message: string }> {
  const screen = screenOf(uiTest, cfg.suiteLabel)
  const addButton = findInRoot(screen, addBtn(cfg.testPrefix))
  if (!addButton) return { ok: false, message: 'Không tìm thấy nút Thêm' }

  uiTest.appendLog({
    id: `add-click-${item.ma}`,
    label: 'Thêm mới',
    status: 'running',
    message: `Thêm ${item.ma}`,
  })
  addButton.click()
  await waitFor(() => document.querySelectorAll('[role="dialog"]').length > 0, 8_000, 80)

  let dialog: HTMLElement
  try {
    dialog = await waitForTopDialog()
  } catch {
    return { ok: false, message: 'Modal thêm không mở' }
  }

  const maField = findFieldByName(dialog, cfg.formMaField)
  const tenField = findFieldByName(dialog, cfg.formTenField)
  const ghiChuField = cfg.formGhiChuField ? findFieldByName(dialog, cfg.formGhiChuField) : null
  if (!tenField) return { ok: false, message: 'Không tìm thấy ô Tên trong form thêm' }
  if (cfg.maRequiredOnCreate !== false && !maField) {
    return { ok: false, message: 'Không tìm thấy ô Mã trong form thêm' }
  }

  if (maField) await typeSlowly(maField, item.ma)
  await typeSlowly(tenField, item.ten)
  if (ghiChuField) await typeSlowly(ghiChuField, item.ghiChu ?? 'Sinh tự động — test thao tác UI')

  try {
    await clickDialogSaveButton(dialog)
    await waitForDialogClosed(dialog)
    return { ok: true, message: `Đã tạo ${item.ma}` }
  } catch (e) {
    return { ok: false, message: (e as { message?: string })?.message || `Lưu thất bại: ${item.ma}` }
  }
}

function buildEntityFeatures(cfg: EntityUiTestModuleConfig, plan: EntityUiTestPlan) {
  const rowSelectMode = cfg.rowSelectMode ?? (cfg.editPattern === 'list-modal' ? 'visible-only' : 'select')
  const deleteMode = cfg.deleteMode ?? (cfg.editPattern === 'list-modal' ? 'direct' : 'confirm')

  return [
    {
      id: 'list',
      label: 'Danh sách',
      run: async (uiTest: UiTestRunner) => {
        await goToCleanList(uiTest, cfg)
        const screen = screenOf(uiTest, cfg.suiteLabel)
        const table = screen.querySelector('table')
        if (!table) return { status: 'fail' as const, message: 'Không thấy bảng danh sách' }
        const dataRows = screen.querySelectorAll('tbody tr[data-ui-test-row-ma], tbody tr')
        if (dataRows.length > 0) {
          return { status: 'ok' as const, message: `Bảng hiển thị ${dataRows.length} dòng (trang hiện tại)` }
        }
        return { status: 'ok' as const, message: 'Bảng đã tải — không có dòng trên trang hiện tại' }
      },
    },
    {
      id: 'create',
      label: 'Thêm mới',
      run: async (uiTest: UiTestRunner) => {
        if (cfg.skipCreate) {
          return { status: 'skip' as const, message: 'Bỏ qua — form phức tạp / cần dữ liệu nghiệp vụ' }
        }
        await goToCleanList(uiTest, cfg)
        const rows = await cfg.fetchAllRows()
        const existingMa = buildMaSet(rows.map((r) => ({ ma: rowMa(r, cfg.rowMaKey) })))
        const existingTen = buildTenSet(rows.map((r) => ({ tenNhom: rowTen(r, cfg.rowTenKey) })))

        let created = 0
        let skipped = 0
        let failed = 0

        for (const item of plan.createItems) {
          if (existingMa.has(normKey(item.ma)) || existingTen.has(normKey(item.ten))) {
            skipped += 1
            continue
          }
          const result = await createOneViaUi(uiTest, cfg, item)
          if (result.ok) {
            created += 1
            existingMa.add(normKey(item.ma))
            existingTen.add(normKey(item.ten))
            uiTest.notifyDataChanged()
          } else {
            failed += 1
            uiTest.appendLog({
              id: `add-fail-${item.ma}`,
              label: 'Thêm mới',
              status: 'fail',
              message: result.message,
            })
          }
        }

        const fresh = await cfg.fetchAllRows()
        refreshEntityUiTestPlan(plan, fresh, cfg.rowMaKey, cfg.maTokenPrefix)
        uiTest.appendLog({
          id: 'plan-after-create',
          label: 'Kế hoạch',
          status: 'ok',
          message: describeEntityUiTestPlan(plan),
        })

        if (failed > 0) {
          return {
            status: 'fail' as const,
            message: `Tạo ${created}, bỏ qua ${skipped}, lỗi ${failed} / ${plan.createItems.length}`,
          }
        }
        return {
          status: 'ok' as const,
          message: `Tạo ${created}, bỏ qua ${skipped} (đã có) / ${plan.createItems.length} — ${describeEntityUiTestPlan(plan)}`,
        }
      },
    },
    {
      id: 'search',
      label: 'Tìm kiếm',
      run: async (uiTest: UiTestRunner) => {
        if (cfg.skipSearch) return { status: 'skip' as const, message: 'Không có ô tìm kiếm' }
        const probeMa = plan.probeMa
        if (!probeMa) return { status: 'skip' as const, message: 'Không có mã trên DB để tìm kiếm' }

        await goToCleanList(uiTest, cfg)
        const screen = screenOf(uiTest, cfg.suiteLabel)
        const searchBox = findInRoot(screen, searchBar(cfg.testPrefix))
        const input = (searchBox?.querySelector('input') ??
          screen.querySelector(`${searchBar(cfg.testPrefix)} input`)) as HTMLInputElement | null
        if (!input) return { status: 'fail' as const, message: 'Không tìm thấy ô tìm kiếm' }

        input.focus()
        await clickElement(input)
        await typeSlowly(input, probeMa)
        input.focus()
        await pressEnter(input)

        await waitFor(() => Boolean(findRow(screen, probeMa)), 10_000, 200)
        if (!findRow(screen, probeMa)) {
          return { status: 'skip' as const, message: `Không thấy dòng ${probeMa} sau khi lọc` }
        }
        return { status: 'ok' as const, message: `Tìm "${probeMa}" — thấy đúng 1 dòng` }
      },
    },
    {
      id: 'view',
      label: 'Xem / chọn dòng',
      run: async (uiTest: UiTestRunner) => {
        const probeMa = plan.probeMa
        if (!probeMa) return { status: 'skip' as const, message: 'Không có dòng trên DB để xem' }

        await goToCleanList(uiTest, cfg)
        uiTest.notifyDataChanged()
        await waitFor(() => Boolean(findRow(screenOf(uiTest, cfg.suiteLabel), probeMa)), 10_000, 200)
        const screen = screenOf(uiTest, cfg.suiteLabel)
        const row = findRow(screen, probeMa)
        if (!row) return { status: 'skip' as const, message: `Không có dòng ${probeMa}` }

        if (rowSelectMode === 'visible-only') {
          const text = row.textContent?.replace(/\s+/g, ' ').trim() ?? probeMa
          return { status: 'ok' as const, message: `Đã thấy dòng ${probeMa} — ${text.slice(0, 120)}` }
        }

        await clickElement(row)
        await waitFor(() => row.getAttribute('data-selected') === 'true', 3_000, 100)
        if (row.getAttribute('data-selected') !== 'true') {
          return { status: 'fail' as const, message: 'Nhấn dòng nhưng không thấy trạng thái chọn' }
        }
        return { status: 'ok' as const, message: `Đã chọn dòng ${probeMa}` }
      },
    },
    {
      id: 'edit',
      label: 'Sửa',
      run: async (uiTest: UiTestRunner) => {
        if (cfg.skipEdit) {
          return { status: 'skip' as const, message: 'Bỏ qua — form sửa phức tạp / phiếu khóa' }
        }
        const probeMa = plan.probeMa
        if (!probeMa) return { status: 'skip' as const, message: 'Không có dòng trên DB để sửa' }

        const rowsBefore = await cfg.fetchAllRows()
        const probeId =
          plan.probeId ??
          rowsBefore.find((r) => normKey(rowMa(r, cfg.rowMaKey)) === normKey(probeMa))?.id?.toString() ??
          null
        if (probeId) plan.probeId = probeId

        await goToCleanList(uiTest, cfg)
        const screen = screenOf(uiTest, cfg.suiteLabel)
        const row = findRow(screen, probeMa)
        if (!row) return { status: 'skip' as const, message: `Không có ${probeMa} để sửa` }

        const editBtn = findRowEditButton(row, cfg.testPrefix, probeMa)
        if (editBtn) {
          await clickElement(editBtn)
        } else if (cfg.editPattern === 'list-modal') {
          await clickElement(row)
        } else {
          return { status: 'fail' as const, message: 'Không tìm thấy nút Sửa trên dòng' }
        }

        if (cfg.editPattern === 'route-modal' || cfg.editPattern === 'route-page') {
          try {
            await waitFor(() => window.location.pathname.includes('/edit/'), 12_000, 150)
          } catch {
            return { status: 'fail' as const, message: 'Không chuyển sang trang sửa' }
          }
        }

        let dialog: HTMLElement | null = null
        let formRoot: HTMLElement

        if (cfg.editPattern === 'route-page') {
          formRoot = screenOf(uiTest, cfg.suiteLabel)
          try {
            await waitFor(() => {
              const tenInput = findFieldByName(formRoot, cfg.formTenField) as HTMLInputElement | null
              return Boolean(tenInput && tenInput.value.trim())
            }, 15_000, 150)
          } catch {
            return { status: 'fail' as const, message: 'Form sửa (trang) chưa sẵn sàng' }
          }
        } else {
          try {
            dialog = await waitForTopDialog()
          } catch {
            return { status: 'fail' as const, message: 'Modal sửa không mở' }
          }
          formRoot = dialog
          try {
            await waitFor(() => {
              const tenInput = findFieldByName(formRoot, cfg.formTenField) as HTMLInputElement | null
              if (!tenInput || !tenInput.value.trim()) return false
              const actions = formRoot.querySelector('.MuiDialogActions-root')
              const saveBtn = actions?.querySelector(
                'button.MuiButton-containedPrimary, button.MuiButton-contained',
              ) as HTMLButtonElement | null
              return Boolean(saveBtn && !saveBtn.disabled)
            }, 15_000, 150)
          } catch {
            return { status: 'fail' as const, message: 'Form sửa chưa sẵn sàng' }
          }
        }

        const tenField = findFieldByName(formRoot, cfg.formTenField)
        const ghiChuField = cfg.formGhiChuField ? findFieldByName(formRoot, cfg.formGhiChuField) : null
        if (!tenField) return { status: 'fail' as const, message: 'Không tìm thấy ô Tên trong form sửa' }

        const existingTen = buildTenSet(rowsBefore.map((r) => ({ tenNhom: rowTen(r, cfg.rowTenKey) })))
        const newTen = generateEditTen(existingTen, cfg.suiteLabel)
        await fillFieldValue(tenField as HTMLInputElement, newTen)
        if (ghiChuField) await fillFieldValue(ghiChuField, `Đã sửa UI test — ${stamp()}`)

        try {
          if (cfg.editPattern === 'route-page') {
            const saveBtn = Array.from(formRoot.querySelectorAll('button.MuiButton-contained')).find((b) =>
              b.textContent?.includes('Lưu') || b.textContent?.toLowerCase().includes('save'),
            ) as HTMLButtonElement | undefined
            if (!saveBtn || saveBtn.disabled) throw new Error('Nút Lưu đang bị khóa')
            await clickElement(saveBtn)
            const path = await resolveListPath(cfg)
            await waitFor(() => window.location.pathname === path, 12_000, 200)
          } else {
            await clickDialogSaveButton(dialog!)
            await waitForDialogClosed(dialog!, 20_000)
            if (cfg.editPattern !== 'list-modal') {
              const path = await resolveListPath(cfg)
              await waitFor(() => window.location.pathname === path, 12_000, 200)
            }
          }
        } catch (e) {
          return { status: 'fail' as const, message: (e as { message?: string })?.message || 'Lưu sửa thất bại' }
        }

        try {
          await waitForAsync(async () => {
            const rows = await cfg.fetchAllRows()
            if (probeId) {
              const updated = rows.find((r) => String(r.id) === String(probeId))
              return Boolean(
                updated &&
                  normKey(rowTen(updated, cfg.rowTenKey)) === normKey(newTen) &&
                  normKey(rowMa(updated, cfg.rowMaKey)) === normKey(probeMa),
              )
            }
            const byMa = rows.find((r) => normKey(rowMa(r, cfg.rowMaKey)) === normKey(probeMa))
            return Boolean(byMa && normKey(rowTen(byMa, cfg.rowTenKey)) === normKey(newTen))
          }, 15_000, 300)
        } catch {
          return { status: 'fail' as const, message: `API chưa phản ánh tên mới "${newTen}"` }
        }

        uiTest.notifyDataChanged()
        await waitForListReady(uiTest, cfg)
        return {
          status: 'ok' as const,
          message: `Đã sửa ${probeMa} — tên → "${newTen}" (mã giữ nguyên)`,
        }
      },
    },
    {
      id: 'delete',
      label: 'Xóa',
      run: async (uiTest: UiTestRunner) => {
        if (cfg.skipDelete || deleteMode === 'skip') {
          return { status: 'skip' as const, message: 'Bỏ qua xóa — không có API / giữ dữ liệu' }
        }
        const deleteMa = plan.deleteMa
        if (!deleteMa) return { status: 'skip' as const, message: 'Không có dòng phù hợp để xóa' }
        if (plan.probeMa && normKey(deleteMa) === normKey(plan.probeMa)) {
          return { status: 'skip' as const, message: `Chỉ có một bản ghi (${deleteMa}) — bỏ qua xóa` }
        }

        await goToCleanList(uiTest, cfg)
        const screen = screenOf(uiTest, cfg.suiteLabel)
        await waitFor(() => Boolean(findRow(screen, deleteMa)), 10_000, 200)

        const row = findRow(screen, deleteMa)
        if (!row) return { status: 'skip' as const, message: `Không có ${deleteMa} trên bảng` }

        const deleteBtn = findRowDeleteButton(row, cfg.testPrefix, deleteMa)
        if (!deleteBtn) return { status: 'fail' as const, message: 'Không tìm thấy nút Xóa trên dòng' }

        await clickElement(deleteBtn)

        if (deleteMode === 'confirm') {
          const confirmDialog = await waitForTopDialog()
          await clickDialogConfirmButton(confirmDialog)
          await waitForDialogClosed(confirmDialog)
        }

        uiTest.notifyDataChanged()
        await waitForListReady(uiTest, cfg)
        await waitFor(() => !findRow(screenOf(uiTest, cfg.suiteLabel), deleteMa), 15_000, 300)
        if (findRow(screenOf(uiTest, cfg.suiteLabel), deleteMa)) {
          return { status: 'fail' as const, message: `Xóa xong nhưng vẫn thấy ${deleteMa}` }
        }
        return { status: 'ok' as const, message: `Đã xóa ${deleteMa} và xác nhận biến mất khỏi danh sách` }
      },
    },
  ]
}

export function getEntityUiTestFeatureCount(cfg: EntityUiTestModuleConfig): number {
  return buildEntityFeatures(cfg, {
    createItems: [],
    probeMa: null,
    probeId: null,
    deleteMa: null,
  }).length
}

export async function runEntityUiTest(
  cfg: EntityUiTestModuleConfig,
  uiTest: UiTestRunner,
): Promise<UiTestScenarioSummary> {
  const rows = await cfg.fetchAllRows()
  const plan = buildEntityUiTestPlan(rows, cfg.rowMaKey, cfg.rowTenKey, cfg.maTokenPrefix)

  uiTest.appendLog({
    id: 'plan-init',
    label: 'Kế hoạch',
    status: 'running',
    message: describeEntityUiTestPlan(plan),
  })

  return runUiTestSuite(uiTest, buildEntityFeatures(cfg, plan), cfg.suiteLabel)
}
